import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useCustomerStore } from '@entities/customer'

import { DisciplineGeneralModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'

interface DisciplineModalMergeReturn {
  refIsOpen: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refKidName: ComputedRef<string>
  refOptions: ComputedRef<AppSelectOption<number>[]>
  refSelectedIds: WritableComputedRef<number[]>
  closeModal: () => void
  confirmSelection: () => void
}

export const useDisciplineModalMerge = (): DisciplineModalMergeReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const customerStore = useCustomerStore()

  const refIsOpen = computed<boolean>(() => generalInfoStore.getActiveModal === DisciplineGeneralModal.Merge)

  const refTitle = computed<string>(() => `Merge copies into discipline ${disciplineStore.getDetails?.code ?? ''}`)

  const refKidName = computed<string>(() => disciplineStore.getDetails?.kid.name ?? '')

  const refOptions = computed<AppSelectOption<number>[]>(() => {
    const details = disciplineStore.getDetails
    const kid = customerStore.getCustomer?.kids.find(item => item.id === details?.kid_id)

    return (kid?.disciplines ?? [])
      .filter(discipline => discipline.id !== details?.id)
      .map(discipline => ({
        value: discipline.id,
        text: `${discipline.code} (id: ${discipline.id})`,
      }))
  })

  const refSelectedIds = computed<number[]>({
    get: () => generalInfoStore.getMergeIds,
    set: (ids: number[]) => generalInfoStore.setMergeIds(ids),
  })

  const closeModal = (): void => {
    generalInfoStore.setActiveModal(null)
  }

  const confirmSelection = (): void => {
    if (!generalInfoStore.getMergeIds.length) return
    generalInfoStore.setActiveModal(DisciplineGeneralModal.MergeConfirm)
  }

  return {
    refIsOpen,
    refTitle,
    refKidName,
    refOptions,
    refSelectedIds,
    closeModal,
    confirmSelection,
  }
}
